/**
 * APIService
 * Centralized API communication for Discovery tool
 */
export class APIService {
    constructor() {
        // Determine API URL based on environment
        this.baseURL = this._getAPIURL();
    }

    _getAPIURL() {
        return window.CAMPAIGNS_CONFIG.DISCOVERY_API_URL;
    }

    async _request(endpoint, options = {}) {
        const url = `${this.baseURL}${endpoint}`;
        const config = {
            headers: {
                'Content-Type': 'application/json',
                ...options.headers
            },
            ...options
        };

        try {
            const response = await fetch(url, config);

            if (!response.ok) {
                let errorMessage;
                try {
                    const errorData = await response.json();
                    errorMessage = errorData.message || errorData.error || `HTTP ${response.status}`;
                } catch (parseError) {
                    errorMessage = `HTTP ${response.status}: ${response.statusText}`;
                }
                throw new Error(`API_REQUEST_FAILED (${endpoint}): ${errorMessage}`);
            }

            return await response.json();
        } catch (error) {
            // Log ALL errors - no suppression. 404s are errors too and must be visible.
            console.error(`[API ERROR] ${endpoint}:`, error.message);
            throw error;
        }
    }

    // Chat with Claude
    async chat(messages, systemPrompt) {
        return this._request('/chat', {
            method: 'POST',
            body: JSON.stringify({
                messages,
                system: systemPrompt,
                max_tokens: 1024,
                temperature: 0.7
            })
        });
    }

    // Save custom product
    async saveProduct(product) {
        return this._request('/save-product', {
            method: 'POST',
            body: JSON.stringify(product)
        });
    }

    // Scrape URL
    async scrapeURL(url) {
        return this._request('/scrape-url', {
            method: 'POST',
            body: JSON.stringify({ url })
        });
    }

    // Load product knowledge
    async getProductKnowledge(productId) {
        return this._request(`/product-knowledge/${productId}`);
    }

    // Get product data
    async getProduct(productId) {
        return this._request(`/product/${productId}`);
    }

    // Get business data
    async getBusiness(productId) {
        return this._request(`/business/${productId}`);
    }

    // Generate campaign
    async generateCampaign(discoveryData) {
        return this._request('/generate-campaign', {
            method: 'POST',
            body: JSON.stringify(discoveryData)
        });
    }

    // Update campaign
    async updateCampaign(campaignId, discoveryData) {
        return this._request(`/update/${campaignId}`, {
            method: 'POST',
            body: JSON.stringify(discoveryData)
        });
    }

    // Load discovery for editing
    async loadDiscovery(campaignId) {
        return this._request(`/load/${campaignId}`);
    }

    // DV2-09: Score Discovery Completion
    async scoreDiscovery(productId) {
        return this._request('/score-discovery', {
            method: 'POST',
            body: JSON.stringify({ productId })
        });
    }

    // DV2-07: Generate Intelligent Interview Flow
    async generateInterviewFlow(productId) {
        return this._request('/generate-interview-flow', {
            method: 'POST',
            body: JSON.stringify({ productId })
        });
    }

    // Health check
    async health() {
        return this._request('/health');
    }
}
